"use client";

import { FC } from "react";
import { Alert, Text, Link } from "@chakra-ui/react";
import NextLink from "next/link";
import SubscriptionCancelledMessage from "./SubscriptionCancelledMessage";
import SubscriptionInactiveMesssage from "./SubscriptionInactiveMesssage";
import EmailNotVerifiedMessage from "./EmailNotVerifiedMessage";
import ActivatedMessage from "./ActivatedMessage";

type Props = {
  activated: boolean;
  errorCode: string | null;
  userId: string | null;
  email: string | null;
};

const LoginAlert: FC<Props> = ({ activated, errorCode, userId, email }) => {
  if (errorCode === "SubscriptionCancelled" && userId && email) {
    return <SubscriptionCancelledMessage userId={userId} email={email} />;
  }

  if (errorCode === "SubscriptionInactive" && userId && email) {
    return <SubscriptionInactiveMesssage userId={userId} email={email} />;
  }

  if (errorCode === "EmailNotVerified" && email) {
    return <EmailNotVerifiedMessage email={email} />;
  }

  if (activated) {
    return <ActivatedMessage />;
  }

  if (errorCode) {
    return (
      <Alert.Root
        status="error"
        m={{ md: -5 }}
        w={{ md: "calc(100% + (var(--chakra-spacing-5) * 2))" }}
      >
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Description>
            {(() => {
              switch (errorCode) {
                case "PatreonMembershipMissing":
                  return (
                    <>
                      <Text>Unable to log in with Patreon.</Text>
                      <Text mt="1em">
                        Check the{" "}
                        <Link
                          asChild
                          variant="underline"
                          color="#2868a5"
                          fontWeight={600}
                        >
                          <NextLink href="/troubleshooting">
                            troubleshooting
                          </NextLink>
                        </Link>{" "}
                        guide for help on getting logged in.
                      </Text>
                    </>
                  );

                case "CredentialsSignin":
                  return (
                    <>
                      <Text>Incorrect email address or password.</Text>
                      <Text mt="1em">
                        If you are a Patreon member, log in with your Patreon
                        account, not your email address.
                      </Text>
                    </>
                  );

                case "SubscriptionCancelled": {
                  return <Text>Your subscription was cancelled.</Text>;
                }

                default:
                  return "Something went wrong.";
              }
            })()}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    );
  }

  return null;
};

export default LoginAlert;
